// ============================================================
// The Shape item: what one drag lays down
// ============================================================

import styles from '../../styles/Battlefield.module.css';
import SliderInput from '../SliderInput';
import MapIcon from '../MapIcon';
import { RadioChip, ModifierToggle } from './controls';

/**
 * A rectangle was never the only room worth building.
 *
 * All four are the same drag with a different predicate over the box, so
 * nothing downstream - preview, fill, erase, zone, dig - learns a new
 * word for any of them.
 */
const FOOTPRINT_SHAPES = [
  { key: 'rect', label: 'Rect', icon: 'rect' },
  { key: 'ellipse', label: 'Ellipse', icon: 'ellipse' },
  { key: 'triangle', label: 'Triangle', icon: 'triangle' },
  { key: 'ngon', label: 'N-gon', icon: 'ngon' },
];

/**
 * Brush TIPS, drawn rather than typed.
 *
 * `▪ ● ◆` are glyphs of visibly different sizes, so three shapes read as
 * three brush SIZES - which is the control immediately below them. Three
 * SVGs in identical boxes say "shape" and nothing else.
 */
const TIPS = [
  { key: 'square', label: 'Square', shape: <rect x="2" y="2" width="10" height="10" /> },
  { key: 'round', label: 'Round', shape: <circle cx="7" cy="7" r="5.5" /> },
  { key: 'diamond', label: 'Diamond', shape: <polygon points="7,1 13,7 7,13 1,7" /> },
];

const BRUSH_SIZES = [1, 3, 5, 7, 9];
const THICKNESSES = [1, 3, 5];

function TipIcon({ shape }) {
  return (
    <svg viewBox="0 0 14 14" className={styles.tipIcon} aria-hidden="true">
      {shape}
    </svg>
  );
}

/**
 * The footprint of a rectangle drag, its sides for an n-gon, and Hollow -
 * a ROOM in one drag, four walls and a floor nobody has to erase.
 */
function RectShapes({ build }) {
  return (
    <div className={styles.brushRow} role="radiogroup" aria-label="Shape">
      {FOOTPRINT_SHAPES.map((sh) => (
        <RadioChip
          key={sh.key}
          checked={build.shape === sh.key}
          testId={`shape-${sh.key}`}
          onClick={() => build.setShape(sh.key)}
          title={sh.label}
        >
          <MapIcon name={sh.icon} /> {sh.label}
        </RadioChip>
      ))}
      {build.shape === 'ngon' && (
        <SliderInput
          label="Sides"
          data-testid="shape-sides"
          min={3}
          max={24}
          value={build.sides}
          onChange={build.setSides}
        />
      )}
      <span className={styles.modifierDivider} aria-hidden="true" />
      <ModifierToggle
        on={build.hollow}
        testId="shape-hollow"
        onClick={() => build.setHollow(!build.hollow)}
        title="Just the edge - a room, not a slab"
      >
        Hollow
      </ModifierToggle>
    </div>
  );
}

function LineThickness({ build }) {
  return (
    <div className={styles.brushRow} role="radiogroup" aria-label="Line thickness">
      {THICKNESSES.map((t) => (
        <RadioChip
          key={t}
          checked={build.thickness === t}
          testId={`line-thickness-${t}`}
          onClick={() => build.setThickness(t)}
        >
          {t} wide
        </RadioChip>
      ))}
    </div>
  );
}

function BrushChoices({ build }) {
  return (
    <>
      <div className={styles.brushRow} role="radiogroup" aria-label="Brush tip">
        {TIPS.map((t) => (
          <RadioChip
            key={t.key}
            checked={build.tip === t.key}
            testId={`tip-${t.key}`}
            onClick={() => build.setTip(t.key)}
            title={t.label}
          >
            <TipIcon shape={t.shape} />
          </RadioChip>
        ))}
      </div>
      <div className={styles.brushRow} role="radiogroup" aria-label="Brush size">
        {BRUSH_SIZES.map((size) => (
          <RadioChip
            key={size}
            checked={build.brushSize === size}
            testId={`brush-size-${size}`}
            onClick={() => build.setBrushSize(size)}
          >
            {size}×{size}
          </RadioChip>
        ))}
      </div>
    </>
  );
}

/** What each gesture offers: a rectangle's footprint, a line's width, a brush's tip and size. */
const GESTURE_CHOICES = { rect: RectShapes, line: LineThickness, brush: BrushChoices };

/**
 * The Shape item. Float makes the wheel lift the slab instead of growing
 * the volume; Shift overrides whichever is set, either way.
 */
export default function ShapePanel({ build }) {
  const Choices = GESTURE_CHOICES[build.gesture];
  return (
    <>
      {Choices && <Choices build={build} />}
      <span className={styles.modifierDivider} aria-hidden="true" />
      <ModifierToggle
        on={build.floatMode}
        testId="build-float"
        onClick={() => build.setFloatMode(!build.floatMode)}
        title="Wheel lifts the slab instead of growing the volume. Shift-wheel does the other one."
      >
        Float
      </ModifierToggle>
      <span className={styles.buildHint}>
        wheel {build.floatMode ? 'lifts' : 'grows'} · shift-wheel the other · right-drag erases
      </span>
    </>
  );
}
